import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  IconButton,
  Switch,
  Tooltip,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  ListItem,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../../contexts/langContext';

interface MaterialSearchResultsProps {
  projectId?: string;
  materials: any[];
  searchQuery: string;
  onDelete?: (materialId: string) => void;
  onToggleEnabled?: (materialId: string) => void;
}

function MaterialSearchResults({ 
  projectId, 
  materials, 
  searchQuery,
  onDelete,
  onToggleEnabled 
}: MaterialSearchResultsProps) {
  const { lang } = useLang();
  const navigate = useNavigate();

  const handleMaterialClick = (materialId: string) => {
    navigate(projectId 
      ? `/projects/${projectId}/materials/${materialId}`
      : `/materials/${materialId}`
    );
  };

  const highlightSearchTerms = (text: string) => {
    if (!searchQuery) return text;
    
    const regex = new RegExp(`(${searchQuery})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => 
      regex.test(part) ? (
        <mark key={i} style={{ backgroundColor: '#fff176', padding: 0 }}>{part}</mark>
      ) : part
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom color="text.secondary">
        {lang === 'en' ? 'Search Results' : '搜索结果'}
        {materials.length > 0 && ` (${materials.length})`}
      </Typography>

      <Stack spacing={2}>
        {materials.map((material) => (
          <Card key={material._id}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <ListItem
                onClick={() => handleMaterialClick(material._id)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                {onToggleEnabled && (
                  <ListItemIcon>
                    <Tooltip title={lang === 'en' ? 'Toggle enabled' : '切换启用状态'}>
                      <Switch
                        edge="start"
                        checked={material.enabled}
                        onChange={(e) => {
                          e.stopPropagation();
                          onToggleEnabled(material._id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Tooltip>
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={
                    <Typography variant="h6" gutterBottom>
                      {highlightSearchTerms(material.fileMetadata?.filename || 'Untitled')}
                    </Typography>
                  }
                  secondary={
                    <>
                      {material.fileMetadata?.parsedText && (
                        <Typography variant="body2" color="text.secondary">
                          {highlightSearchTerms(material.fileMetadata.parsedText.substring(0, 200))}
                          {material.fileMetadata.parsedText.length > 200 && '...'}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        {new Date(material.createdAt).toLocaleDateString()}
                      </Typography>
                    </>
                  }
                />
                {onDelete && (
                  <ListItemSecondaryAction>
                    <Tooltip title={lang === 'en' ? 'Delete material' : '删除材料'}>
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(material._id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            </CardContent>
          </Card>
        ))}

        {materials.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {lang === 'en' ? 'No results found' : '未找到结果'}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

export default MaterialSearchResults; 